import React, { useRef, useEffect, useState } from 'react';
import { string, arrayOf, bool, func, number, object } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape, FormattedDate } from '../../util/reactIntl';
import dropWhile from 'lodash/dropWhile';
import classNames from 'classnames';
import {
  NamedLink,
  Avatar,
  InlineTextButton,
  ReviewRating,
  UserDisplayName,
  PackagePrice,
  PricingModelBadge,
} from '../../components';
import { formatDate } from '../../util/dates';
import { isValidEnquiryPackage } from '../../util/listingPackages';
import { ensureTransaction, ensureUser, ensureListing } from '../../util/data';
import { customOfferDataFromTransaction } from '../../util/customOffer';
import {
  TRANSITION_ACCEPT,
  TRANSITION_CANCEL,
  TRANSITION_CANCEL_PAYMENT,
  TRANSITION_COMPLETE,
  TRANSITION_DECLINE,
  TRANSITION_EXPIRE,
  TRANSITION_RELEASE_FUNDS,
  TRANSITION_JOB_DONE_RELEASE_FUNDS,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_PROVIDER,
  TRANSITION_ACCEPT_OFFER,
  TRANSITION_DECLINE_OFFER,
  TRANSITION_EXPIRE_OFFER,
  TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY,
  TRANSITION_MAKE_CUSTOM_PRO_OFFER,
  TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
  TRANSITION_MAKE_CUSTOM_OFFER,
  TRANSITION_JOB_DONE_PRO,
  TRANSITION_JOB_ACCEPTED,
  TRANSITION_SKIP_TO_REVIEWS,
  transitionIsReviewed,
  txIsDelivered,
  txIsProReadyAccepted,
  txIsInFirstReviewBy,
  txIsReviewed,
  isCustomerReview,
  isProviderReview,
  txRoleIsProvider,
  txRoleIsCustomer,
  getUserTxRole,
  isRelevantPastTransition,
  TRANSITION_JOB_DONE,
  TRANSITION_JOB_DONE_CUSTOMER,
  TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  TRANSITION_JOB_DONE_INVOICE,
  TRANSITION_ACCEPT_JOB_INVOICE,
  TRANSITION_JOB_DONE_ACCEPT_INVOICE,
  TRANSITION_CANCEL_INVOICE,
} from '../../util/transaction';
import { propTypes } from '../../util/types';
import * as log from '../../util/log';
import { apiBaseUrl } from '../../util/api';
import { isFreedomlyUrl } from '../../util/urlHelpers';
import { URL_REGEX } from '../../util/richText';
import AttachmentList from '../AttachmentList/AttachmentList';
import LeavingSiteModal from './LeavingSiteModal';
import { IoMdCheckmarkCircle, IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { formatMoney } from '../../util/currency';
import { formatDateOnly } from '../../util/dates';
import { types as sdkTypes } from '../../util/sdkLoader';
import { offerTotals, resolutionForOffer } from '../../util/vat';
import { SecondaryButton, PrimaryButton } from '../../components';

import css from './ActivityFeed.css';
import transactionCss from '../TransactionPanel/TransactionPanel.css';

const { Money } = sdkTypes;

// Split a plain-text string into text + clickable link nodes. URL_REGEX has a capturing
// group, so String.split keeps the matched URLs as separate tokens. Each link calls
// onLinkClick so callers can intercept external links (e.g. show the "leaving" modal).
const linkifyText = (text, keyPrefix, onLinkClick, linkClass) => {
  if (typeof text !== 'string' || text === '') {
    return text;
  }
  return text.split(URL_REGEX).map((part, i) => {
    const isUrl = /^https?:\/\//i.test(part);
    if (!isUrl) {
      return part;
    }
    return (
      <a
        key={`${keyPrefix}-link-${i}`}
        href={part}
        className={linkClass}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => onLinkClick(e, part)}
      >
        {part}
      </a>
    );
  });
};

// A legacy attachment message references a MongoDB-stored file with an inline
// token: <<<mongoId|filename>>>. New messages use Sharetribe file attachments
// instead, but historical conversations must keep rendering these.
// Exported for tests.
const LEGACY_ATTACHMENT_TOKEN_REGEX = /<<<([^|>]+)\|([^>]*?)>>>/;
export const parseLegacyAttachmentToken = content => {
  if (typeof content !== 'string') {
    return null;
  }
  const match = content.match(LEGACY_ATTACHMENT_TOKEN_REGEX);
  if (!match) {
    return null;
  }
  const [token, attachmentId, attachmentName] = match;
  return {
    attachmentId,
    attachmentName,
    pre: content.slice(0, match.index),
    post: content.slice(match.index + token.length),
  };
};

// Sentinel content stored for attachment-only messages (no typed text). Sharetribe
// requires non-empty message content, so the sender (TransactionPanel) stores this
// token; the frontend renders it as a localized "Attachments" label in the viewing
// user's own locale. Exported so the sender and this renderer agree on the value.
export const ATTACHMENTS_ONLY_MESSAGE = '<<<attachments>>>';

// Render a message's text content: resolve the attachment-only placeholder token
// and the legacy `<<<id|name>>>` attachment token (if any), and linkify any URLs in
// the surrounding text. Legacy attachment links point to our own API, so they are
// not intercepted.
const renderMessageContent = (content, { onLinkClick, linkClass }) => {
  if (typeof content !== 'string') {
    return content;
  }

  if (content === ATTACHMENTS_ONLY_MESSAGE) {
    return <FormattedMessage id="ActivityFeed.attachmentsOnlyMessage" />;
  }

  const legacyAttachment = parseLegacyAttachmentToken(content);
  if (legacyAttachment) {
    const { attachmentId, attachmentName, pre, post } = legacyAttachment;
    return (
      <span>
        {linkifyText(pre, 'pre', onLinkClick, linkClass)}
        <a
          href={`${apiBaseUrl()}/api/show-message-attachment/${attachmentId}`}
          className={transactionCss.attachmentLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {attachmentName}
        </a>
        {linkifyText(post, 'post', onLinkClick, linkClass)}
      </span>
    );
  }

  return linkifyText(content, 'msg', onLinkClick, linkClass);
};

const Message = props => {
  const {
    message,
    intl,
    onLinkClick,
    onDownloadAttachment,
    downloadAttachmentInProgressIds,
    onOpenAttachment,
    onFetchAttachmentPreview,
    attachmentPreviewUrls,
  } = props;
  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });
  const text = renderMessageContent(message.attributes.content, {
    onLinkClick,
    linkClass: transactionCss.messageLink,
  });

  return (
    <div className={transactionCss.message}>
      <Avatar className={transactionCss.messageAvatar} user={message.sender} />
      <div className={transactionCss.messageContentWrapper}>
        <div className={transactionCss.messageSenderDate}>
          <FormattedMessage
            id="TransactionPanel.conversationWith"
            values={{ name: message.sender.attributes.profile.displayName }}
          />
          <p className={transactionCss.messageDate}>
            {formatDate(intl, todayString, message.attributes.createdAt)}
          </p>
        </div>
        <div className={transactionCss.messageContent}>
          {text ? <p className={transactionCss.messageText}>{text}</p> : null}
          <AttachmentList
            attachments={message.publicFileAttachments}
            intl={intl}
            onDownloadAttachment={onDownloadAttachment}
            downloadAttachmentInProgressIds={downloadAttachmentInProgressIds}
            onOpenAttachment={onOpenAttachment}
            onFetchAttachmentPreview={onFetchAttachmentPreview}
            attachmentPreviewUrls={attachmentPreviewUrls}
          />
        </div>
      </div>
    </div>
  );
};

Message.propTypes = {
  message: propTypes.message.isRequired,
  intl: intlShape.isRequired,
  onLinkClick: func.isRequired,
  onDownloadAttachment: func.isRequired,
  downloadAttachmentInProgressIds: arrayOf(string).isRequired,
  onOpenAttachment: func.isRequired,
  onFetchAttachmentPreview: func.isRequired,
  attachmentPreviewUrls: object.isRequired,
};

const OwnMessage = props => {
  const {
    message,
    intl,
    onLinkClick,
    onDownloadAttachment,
    downloadAttachmentInProgressIds,
    onOpenAttachment,
    onFetchAttachmentPreview,
    attachmentPreviewUrls,
  } = props;
  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });
  const text = renderMessageContent(message.attributes.content, {
    onLinkClick,
    linkClass: transactionCss.messageLink,
  });

  return (
    <div className={transactionCss.ownMessage}>
      <div className={transactionCss.messageContentWrapper}>
        <div className={transactionCss.messageSenderDate}>
          <p className={transactionCss.messageDate}>
            {formatDate(intl, todayString, message.attributes.createdAt)}
          </p>
        </div>

        <div className={transactionCss.messageContent}>
          {text ? <p className={transactionCss.messageText}>{text}</p> : null}
          <AttachmentList
            attachments={message.publicFileAttachments}
            intl={intl}
            onDownloadAttachment={onDownloadAttachment}
            downloadAttachmentInProgressIds={downloadAttachmentInProgressIds}
            onOpenAttachment={onOpenAttachment}
            onFetchAttachmentPreview={onFetchAttachmentPreview}
            attachmentPreviewUrls={attachmentPreviewUrls}
          />
        </div>
      </div>
    </div>
  );
};

OwnMessage.propTypes = {
  message: propTypes.message.isRequired,
  intl: intlShape.isRequired,
  onLinkClick: func.isRequired,
  onDownloadAttachment: func.isRequired,
  downloadAttachmentInProgressIds: arrayOf(string).isRequired,
  onOpenAttachment: func.isRequired,
  onFetchAttachmentPreview: func.isRequired,
  attachmentPreviewUrls: object.isRequired,
};

const Review = props => {
  const { content, rating } = props;
  return (
    <div>
      <p className={css.reviewContent}>{content}</p>
      {rating ? (
        <ReviewRating
          reviewStarClassName={css.reviewStar}
          className={css.reviewStars}
          rating={rating}
        />
      ) : null}
    </div>
  );
};

Review.propTypes = {
  content: string.isRequired,
  rating: number.isRequired,
};

export const CustomOfferActivity = ({
  customOfferData,
  isCustomer,
  transaction,
  vatPreview,
  intl,
  onOpenOfferModal,
  isPending,
}) => {
  if (!customOfferData) return null;

  const offerResolution = resolutionForOffer(transaction, vatPreview);
  const totals = offerTotals({
    lineItems: customOfferData.lineItems,
    isPending,
    resolution: offerResolution,
    customerPrice: customOfferData.amount,
  });
  const showTotalColumn = !isCustomer;

  const handleOpenModal = e => {
    if (onOpenOfferModal) {
      e.preventDefault();
      e.stopPropagation();
      onOpenOfferModal();
    }
  };

  // Truncate description if it's longer than 150 characters
  const truncateDescription = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className={css.customOfferActivity}>
      {/* Description */}
      {customOfferData.description && (
        <div className={css.offerDescription}>
          <p className={css.offerDescriptionText}>
            {truncateDescription(customOfferData.description)}
          </p>
        </div>
      )}

      {/* 3-column pricing card */}
      <div className={css.offerDetailsCard}>
        {/* Column 1: the schedule. Offers carrying a start date show the whole
            range; ones made before that field existed keep the single date. */}
        {customOfferData.deliveryDate && (
          <div className={css.offerDetailsCardColumn}>
            <span className={css.offerCardLabel}>
              <FormattedMessage
                id={
                  customOfferData.startDate ? 'ProcessSteps.schedule' : 'ProcessSteps.deliveryDate'
                }
              />
            </span>
            <span className={css.offerCardValue}>
              {customOfferData.startDate
                ? `${formatDateOnly(
                    intl,
                    intl.formatMessage({ id: 'ActivityFeed.today' }),
                    new Date(customOfferData.startDate)
                  )} \u2013 ${formatDateOnly(
                    intl,
                    intl.formatMessage({ id: 'ActivityFeed.today' }),
                    new Date(customOfferData.deliveryDate)
                  )}`
                : formatDateOnly(
                    intl,
                    intl.formatMessage({ id: 'ActivityFeed.today' }),
                    new Date(customOfferData.deliveryDate)
                  )}
            </span>
          </div>
        )}

        {/* Column 2: Price */}
        <div className={css.offerDetailsCardColumn}>
          <span className={css.offerCardLabel}>
            <FormattedMessage
              id={isCustomer ? 'ProcessSteps.totalToPay' : 'ProcessSteps.customerPrice'}
            />
          </span>
          <div className={css.offerCardValueRow}>
            <span className={css.offerCardValue}>
              {formatMoney(intl, new Money(customOfferData.amount, 'EUR'))}
            </span>
          </div>
        </div>

        {/* Column 3: Total / You Earn. The customer's VAT-inclusive total
            waits for the preview; the provider's net never depends on it. */}
        {showTotalColumn && (
          <div className={css.offerDetailsCardColumn}>
            <span className={css.offerCardLabel}>
              <FormattedMessage
                id={isCustomer ? 'ProcessSteps.totalToPayShort' : 'ProcessSteps.youWillEarnFinal'}
              />
            </span>
            <div className={css.offerCardValueRow}>
              <span className={css.offerCardValue}>
                {isCustomer
                  ? formatMoney(
                      intl,
                      customOfferData.payinTotal || new Money(totals.customerTotal, 'EUR')
                    )
                  : formatMoney(intl, new Money(totals.providerNet, 'EUR'))}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Full-width button */}
      <div className={css.offerFullWidthButton}>
        <button type="button" onClick={handleOpenModal}>
          <FormattedMessage id="ProcessSteps.viewFullDetails" />
        </button>
      </div>
    </div>
  );
};

CustomOfferActivity.propTypes = {
  customOfferData: object,
  isCustomer: bool.isRequired,
  transaction: object,
  vatPreview: object,
  intl: intlShape.isRequired,
  onOpenOfferModal: func.isRequired,
  isPending: bool,
};

const hasUserLeftAReviewFirst = (userRole, transaction) => {
  // Because function txIsInFirstReviewBy uses isCustomer to check in which state the reviews are
  // we should also use isCustomer insted of isProvider
  const isCustomer = txRoleIsCustomer(userRole);
  return txIsInFirstReviewBy(transaction, isCustomer);
};

// Whether to show the current user's own not-yet-published review under their
// review transition (double-blind phase: the tx still waits for the other
// party's review). `ownReview` is fetched via our own API by the
// TransactionPage duck — the Marketplace API nulls pending review content
// even for the author.
export const shouldShowOwnPendingReview = (transaction, transition, ownRole, ownReview) => {
  const isFirstReviewTransition =
    transition.transition === TRANSITION_REVIEW_1_BY_CUSTOMER ||
    transition.transition === TRANSITION_REVIEW_1_BY_PROVIDER;
  return !!(
    ownReview &&
    isFirstReviewTransition &&
    transition.by === ownRole &&
    txIsInFirstReviewBy(transaction, txRoleIsCustomer(ownRole))
  );
};

const resolveTransitionMessage = (
  transaction,
  transition,
  listingTitle,
  ownRole,
  otherUsersName,
  intl,
  onOpenReviewModal,
  onOpenOfferModal,
  onOpenInvoiceModal
) => {
  const isOwnTransition = transition.by === ownRole;
  const currentTransition = transition.transition;
  const displayName = otherUsersName;
  const newTransactionId = transaction.attributes.protectedData?.newTransactionId;
  const isCustomer = ownRole === 'customer';
  const customOfferDeliveryDate = transaction.attributes.protectedData?.customOfferDeliveryDate;
  const customOfferStartDate = transaction.attributes.protectedData?.customOfferStartDate;
  const projectDeliveryDate = transaction.attributes.protectedData?.projectBidDeliveryDate;

  // Offers made before the start date existed name the delivery date alone.
  const deliveryDateElement = () =>
    customOfferDeliveryDate ? (
      <>
        {customOfferStartDate ? (
          <>
            <FormattedMessage id="ActivityFeed.startDate" />
            <FormattedDate value={customOfferStartDate} />
          </>
        ) : null}
        <FormattedMessage id="ActivityFeed.deliveryDate" />
        <FormattedDate value={customOfferDeliveryDate} />
      </>
    ) : null;

  const projectDeliveryDateElement = () =>
    projectDeliveryDate ? (
      <>
        <FormattedMessage id="ActivityFeed.deliveryDate" />
        <FormattedDate value={projectDeliveryDate} />
      </>
    ) : null;

  switch (currentTransition) {
    case TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY:
      return isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownTransitionRequest" />
      ) : (
        <>
          <FormattedMessage
            id="ActivityFeed.transitionProjectBid"
            values={{ displayName, listingTitle }}
          />
          {projectDeliveryDateElement()}
        </>
      );
    case TRANSITION_MAKE_CUSTOM_OFFER:
      return isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownCustomOffer" />
      ) : (
        <FormattedMessage id="ActivityFeed.customOfferReceived" values={{ displayName }} />
      );
    case TRANSITION_MAKE_CUSTOM_PRO_OFFER:
      return isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownTransitionRequest" />
      ) : (
        <>
          <FormattedMessage
            id="ActivityFeed.transitionProjectBid"
            values={{ displayName, listingTitle }}
          />
        </>
      );
    case TRANSITION_CONFIRM_PAYMENT:
      return isCustomer ? (
        <FormattedMessage id="ActivityFeed.customerWaitingWork" />
      ) : (
        <FormattedMessage id="TransactionSteps.providerCompleteWorkTitle" />
      );
    case TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE:
    case TRANSITION_ACCEPT_CUSTOM_PRO_OFFER:
      return isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownTransitionAccepted" />
      ) : (
        <FormattedMessage
          id="ActivityFeed.transitionAccepted"
          values={{ displayName, listingTitle }}
        />
      );
    case TRANSITION_ACCEPT_OFFER:
      return isOwnTransition ? (
        <NamedLink
          name={isCustomer ? 'SaleDetailsPage' : 'OrderDetailsPage'}
          params={{ id: newTransactionId }}
        >
          <FormattedMessage id="ActivityFeed.transitionAcceptProject" values={{ displayName }} />
        </NamedLink>
      ) : (
        <NamedLink
          name={isCustomer ? 'SaleDetailsPage' : 'OrderDetailsPage'}
          params={{ id: newTransactionId }}
        >
          <FormattedMessage id="ActivityFeed.transitionAcceptProject" values={{ displayName }} />
        </NamedLink>
      );
    case TRANSITION_DECLINE_OFFER:
      return isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownTransitionDecline" values={{ displayName }} />
      ) : (
        <FormattedMessage id="ActivityFeed.transitionDecline" values={{ displayName }} />
      );
    case TRANSITION_EXPIRE_OFFER:
      return !isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownTransitionExpire" values={{ displayName }} />
      ) : (
        <FormattedMessage id="ActivityFeed.transitionExpire" values={{ displayName }} />
      );
    case TRANSITION_ACCEPT:
      return isOwnTransition ? (
        <>
          <FormattedMessage id="ActivityFeed.ownTransitionStarted" values={{ displayName }} />
          {deliveryDateElement()}
        </>
      ) : (
        <>
          <FormattedMessage id="ActivityFeed.transitionAccept" values={{ displayName }} />
          {deliveryDateElement()}
        </>
      );
    case TRANSITION_DECLINE:
      return isOwnTransition ? (
        <FormattedMessage id="ActivityFeed.ownTransitionDecline" />
      ) : (
        <FormattedMessage id="ActivityFeed.transitionDecline" values={{ displayName }} />
      );
    case TRANSITION_EXPIRE:
      return txRoleIsProvider(ownRole) ? (
        <FormattedMessage id="ActivityFeed.ownTransitionExpire" />
      ) : (
        <FormattedMessage id="ActivityFeed.transitionExpire" values={{ displayName }} />
      );
    case TRANSITION_CANCEL_INVOICE:
    case TRANSITION_CANCEL:
      return <FormattedMessage id="ActivityFeed.transitionCancel" />;

    case TRANSITION_CANCEL_PAYMENT:
      return <FormattedMessage id="ActivityFeed.transitionCancelPayment" />;

    case TRANSITION_COMPLETE:
      // const reviewPeriodJustStarted = txIsDelivered(transaction);

      // const reviewAsFirstLink = reviewPeriodJustStarted ? (
      //   <InlineTextButton onClick={onOpenReviewModal}>
      //     <FormattedMessage id="ActivityFeed.leaveAReview" values={{ displayName }} />
      //   </InlineTextButton>
      // ) : null;
      return txRoleIsProvider(ownRole) ? (
        <div>
          <FormattedMessage id="ActivityFeed.transitionCompleteSeller" values={{ displayName }} />
        </div>
      ) : !txRoleIsProvider(!ownRole) ? (
        <div>
          <FormattedMessage id="ActivityFeed.transitionCompleteBuyer" values={{ displayName }} />
        </div>
      ) : null;

    case TRANSITION_RELEASE_FUNDS:
    case TRANSITION_JOB_DONE_RELEASE_FUNDS:
      // Show the leave a review link if the state is delivered and if the current user is the first to leave a review
      // const reviewPeriodJustStarted = txIsDelivered(transaction);
      const reviewPeriodJustStarted = txIsDelivered(transaction);

      const reviewAsFirstLink = reviewPeriodJustStarted ? (
        <InlineTextButton onClick={onOpenReviewModal}>
          <FormattedMessage id="ActivityFeed.leaveAReview" values={{ displayName }} />
        </InlineTextButton>
      ) : null;

      // Check provider's account_role to determine which message to show
      if (txRoleIsProvider(ownRole)) {
        const provider = transaction.provider;
        const providerAccountRole = provider?.attributes?.profile?.publicData?.account_role;

        if (providerAccountRole === 'freelancer') {
          return <FormattedMessage id="ActivityFeed.transitionFundsReleasedSellerFreelancer" />;
        }

        return (
          <FormattedMessage
            id="ActivityFeed.transitionFundsReleasedSeller"
            values={{ invoiceLink: '' }}
          />
        );
      }

      return <FormattedMessage id="ActivityFeed.transitionFundsReleasedBuyer" />;

    case TRANSITION_ACCEPT_JOB_INVOICE:
    case TRANSITION_JOB_DONE_ACCEPT_INVOICE:
    case TRANSITION_JOB_ACCEPTED:
    case TRANSITION_SKIP_TO_REVIEWS:
      // Show the leave a review link if the state is delivered and if the current user is the first to leave a review
      // const reviewPeriodJustStarted = txIsDelivered(transaction);
      const reviewPeriodJustStartedPro =
        txIsProReadyAccepted(transaction) || txIsDelivered(transaction);

      const reviewAsFirstLinkPro = reviewPeriodJustStartedPro ? (
        <InlineTextButton onClick={onOpenReviewModal}>
          <FormattedMessage id="ActivityFeed.leaveAReview" values={{ displayName }} />
        </InlineTextButton>
      ) : null;

      // Check provider's account_role to determine which message to show
      if (txRoleIsProvider(ownRole)) {
        const provider = transaction.provider;
        const providerAccountRole = provider?.attributes?.profile?.publicData?.account_role;

        if (providerAccountRole === 'freelancer') {
          return <FormattedMessage id="ActivityFeed.transitionFundsReleasedSellerProFreelancer" />;
        }

        return (
          <FormattedMessage
            id="ActivityFeed.transitionFundsReleasedSellerPro"
            values={{ invoiceLink: '' }}
          />
        );
      }

      return <FormattedMessage id="ActivityFeed.transitionFundsReleasedBuyerPro" />;

    case TRANSITION_REVIEW_1_BY_PROVIDER:
    case TRANSITION_REVIEW_1_BY_CUSTOMER:
      if (isOwnTransition) {
        return <FormattedMessage id="ActivityFeed.ownTransitionReview" values={{ displayName }} />;
      } else {
        // show the leave a review link if current user is not the first
        // one to leave a review
        const reviewPeriodIsOver = txIsReviewed(transaction);
        const userHasLeftAReview = hasUserLeftAReviewFirst(ownRole, transaction);
        const reviewAsSecondLink = !(reviewPeriodIsOver || userHasLeftAReview) ? (
          <InlineTextButton onClick={onOpenReviewModal}>
            <FormattedMessage id="ActivityFeed.leaveAReviewSecond" values={{ displayName }} />
          </InlineTextButton>
        ) : null;
        return (
          <FormattedMessage
            id="ActivityFeed.transitionReview"
            values={{ displayName, reviewLink: reviewAsSecondLink }}
          />
        );
      }
    case TRANSITION_REVIEW_2_BY_PROVIDER:
    case TRANSITION_REVIEW_2_BY_CUSTOMER:
      if (isOwnTransition) {
        return <FormattedMessage id="ActivityFeed.ownTransitionReview" values={{ displayName }} />;
      } else {
        return (
          <FormattedMessage
            id="ActivityFeed.transitionReview"
            values={{ displayName, reviewLink: null }}
          />
        );
      }
    case TRANSITION_JOB_DONE:
    case TRANSITION_JOB_DONE_CUSTOMER:
      return txRoleIsProvider(ownRole) ? (
        <FormattedMessage id="ActivityFeed.transitionJobDoneProvider" />
      ) : (
        <FormattedMessage id="ActivityFeed.transitionJobDoneCustomer" />
      );
    case TRANSITION_JOB_DONE_INVOICE:
    case TRANSITION_JOB_DONE_PRO:
      return txRoleIsProvider(ownRole) ? (
        <FormattedMessage id="ActivityFeed.transitionJobDoneProvider" />
      ) : (
        <FormattedMessage id="ActivityFeed.transitionJobDoneCustomer" />
      );

    default:
      log.error(new Error('Unknown transaction transition type'), 'unknown-transition-type', {
        transitionType: currentTransition,
      });
      return '';
  }
};

const reviewByAuthorId = (transaction, userId) => {
  return transaction.reviews.filter(
    r => !r.attributes.deleted && r.author.id.uuid === userId.uuid
  )[0];
};

const Transition = props => {
  const {
    transition,
    transaction,
    currentUser,
    intl,
    onOpenReviewModal,
    onOpenOfferModal,
    onOpenInvoiceModal,
    stepActions,
    actionInProgress,
    ownReview,
    vatPreview,
  } = props;

  const currentTransaction = ensureTransaction(transaction);
  const customer = currentTransaction.customer;
  const provider = currentTransaction.provider;

  const deletedListing = intl.formatMessage({
    id: 'ActivityFeed.deletedListing',
  });
  const listingTitle = currentTransaction.listing.attributes.deleted
    ? deletedListing
    : currentTransaction.listing.attributes.title;
  const lastTransition = currentTransaction.attributes.lastTransition;

  const ownRole = getUserTxRole(currentUser.id, currentTransaction);
  const isCustomer = txRoleIsCustomer(ownRole);

  const otherUsersName = txRoleIsProvider(ownRole) ? (
    <UserDisplayName user={customer} intl={intl} />
  ) : (
    <UserDisplayName user={provider} intl={intl} />
  );

  const transitionMessage = resolveTransitionMessage(
    transaction,
    transition,
    listingTitle,
    ownRole,
    otherUsersName,
    intl,
    onOpenReviewModal,
    onOpenOfferModal,
    onOpenInvoiceModal
  );

  const currentTransition = transition.transition;

  // Check if this is a custom offer transition
  const isCustomOfferTransition =
    currentTransition === TRANSITION_MAKE_CUSTOM_OFFER ||
    currentTransition === TRANSITION_MAKE_CUSTOM_PRO_OFFER;
  const customOfferData = isCustomOfferTransition
    ? customOfferDataFromTransaction(currentTransaction, isCustomer)
    : null;

  const deletedReviewContent = intl.formatMessage({ id: 'ActivityFeed.deletedReviewContent' });
  let reviewComponent = null;

  if (transitionIsReviewed(lastTransition)) {
    if (isCustomerReview(currentTransition)) {
      const review = reviewByAuthorId(currentTransaction, customer.id);
      reviewComponent = review ? (
        <Review content={review.attributes.content} rating={review.attributes.rating} />
      ) : (
        <Review content={deletedReviewContent} />
      );
    } else if (isProviderReview(currentTransition)) {
      const review = reviewByAuthorId(currentTransaction, provider.id);
      reviewComponent = review ? (
        <Review content={review.attributes.content} rating={review.attributes.rating} />
      ) : (
        <Review content={deletedReviewContent} />
      );
    }
  } else if (shouldShowOwnPendingReview(currentTransaction, transition, ownRole, ownReview)) {
    reviewComponent = (
      <div>
        <Review content={ownReview.content} rating={ownReview.rating} />
        <p className={css.ownPendingReviewNote}>
          <FormattedMessage
            id="ActivityFeed.ownPendingReviewNote"
            values={{ displayName: otherUsersName }}
          />
        </p>
      </div>
    );
  }

  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });

  // Check if this is a pending custom offer
  const isCustomOfferPending = isCustomOfferTransition && lastTransition === currentTransition;

  // Check if action is needed for this transition
  const needsAction =
    (lastTransition === currentTransition && stepActions) || (isCustomOfferPending && isCustomer);

  // Determine instruction text and action button based on transition
  let actionButton = null;
  let instructionText = null;

  if (needsAction) {
    switch (currentTransition) {
      case TRANSITION_JOB_DONE:
      case TRANSITION_JOB_DONE_CUSTOMER:
        if (isCustomer && stepActions.onReleaseFunds) {
          instructionText = <FormattedMessage id="TransactionSteps.customerReleaseFunds" />;
          actionButton = (
            <PrimaryButton onClick={stepActions.onReleaseFunds} inProgress={actionInProgress}>
              <FormattedMessage id="TransactionSteps.releaseFundsButton" />
            </PrimaryButton>
          );
        }
        // Provider has already completed the job - no action needed
        break;
      case TRANSITION_JOB_DONE_PRO:
        // After provider marks job done in pro process, customer needs to accept
        if (isCustomer && stepActions.onAcceptProJob) {
          instructionText = <FormattedMessage id="TransactionSteps.customerAcceptJob" />;
          actionButton = (
            <PrimaryButton onClick={stepActions.onAcceptProJob} inProgress={actionInProgress}>
              <FormattedMessage id="TransactionSteps.acceptJobButton" />
            </PrimaryButton>
          );
        }
        break;
      case TRANSITION_RELEASE_FUNDS:
      case TRANSITION_JOB_DONE_RELEASE_FUNDS:
        // Review actions are now shown as a separate item, not here
        // No action needed on the release funds transition itself
        break;
      case TRANSITION_JOB_ACCEPTED:
        // Reviews are handled by the separate ReviewAction component
        // No action needed here - this keeps the transition as a normal green checkmark
        break;
      case TRANSITION_REVIEW_1_BY_CUSTOMER:
      case TRANSITION_REVIEW_1_BY_PROVIDER:
      case TRANSITION_SKIP_TO_REVIEWS:
        // Reviews are handled by the separate ReviewAction component
        // No action needed here - this keeps the transition as a normal green checkmark
        break;
      case TRANSITION_REVIEW_2_BY_CUSTOMER:
      case TRANSITION_REVIEW_2_BY_PROVIDER:
        // Both parties have completed their reviews - no action needed
        break;
      case TRANSITION_CONFIRM_PAYMENT:
        if (!isCustomer && stepActions.onJobReady) {
          instructionText = <FormattedMessage id="TransactionSteps.providerCompleteWork" />;
          actionButton = (
            <PrimaryButton onClick={stepActions.onJobReady} inProgress={actionInProgress}>
              <FormattedMessage id="TransactionSteps.markJobDone" />
            </PrimaryButton>
          );
        } else if (isCustomer && stepActions.onJobDoneReleaseFunds) {
          instructionText = <FormattedMessage id="TransactionSteps.customerCanReleaseFunds" />;
          actionButton = (
            <PrimaryButton
              onClick={stepActions.onJobDoneReleaseFunds}
              inProgress={actionInProgress}
            >
              <FormattedMessage id="TransactionSteps.releaseFundsButton" />
            </PrimaryButton>
          );
        }
        break;
      case TRANSITION_ACCEPT_CUSTOM_PRO_OFFER:
        if (!isCustomer && stepActions.onJobReadyPro) {
          instructionText = <FormattedMessage id="TransactionSteps.providerCompleteWork" />;
          actionButton = (
            <PrimaryButton onClick={stepActions.onJobReadyPro} inProgress={actionInProgress}>
              <FormattedMessage id="TransactionSteps.markJobDone" />
            </PrimaryButton>
          );
        } else if (isCustomer) {
          // Customer must wait for provider to mark job complete in pro process
          instructionText = (
            <FormattedMessage id="TransactionSteps.customerWaitForProviderToMarkComplete" />
          );
          // No action button for customer - they must wait
        }
        break;
      case TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE:
        if (!isCustomer && stepActions.onJobDoneInvoicePath) {
          instructionText = <FormattedMessage id="TransactionSteps.providerCompleteWork" />;
          actionButton = (
            <PrimaryButton onClick={stepActions.onJobDoneInvoicePath} inProgress={actionInProgress}>
              <FormattedMessage id="TransactionSteps.markJobDone" />
            </PrimaryButton>
          );
        } else if (isCustomer && stepActions.onJobDoneAcceptInvoice) {
          instructionText = <FormattedMessage id="TransactionSteps.customerCanAcceptInvoice" />;
          actionButton = (
            <PrimaryButton
              onClick={stepActions.onJobDoneAcceptInvoice}
              inProgress={actionInProgress}
            >
              <FormattedMessage id="TransactionSteps.acceptJobButton" />
            </PrimaryButton>
          );
        }
        break;
      case TRANSITION_JOB_DONE_INVOICE:
        if (isCustomer && stepActions.onAcceptJobInvoicePath) {
          instructionText = <FormattedMessage id="TransactionSteps.customerAcceptJob" />;
          actionButton = (
            <PrimaryButton
              onClick={stepActions.onAcceptJobInvoicePath}
              inProgress={actionInProgress}
            >
              <FormattedMessage id="TransactionSteps.acceptJobButton" />
            </PrimaryButton>
          );
        }
        break;
      case TRANSITION_ACCEPT_JOB_INVOICE:
      case TRANSITION_JOB_DONE_ACCEPT_INVOICE:
        // Reviews are handled by the separate ReviewAction component.
        break;
    }
  }

  const hasExpandedContent =
    (isCustomOfferTransition && customOfferData) ||
    (needsAction && actionButton) ||
    reviewComponent;
  const isActionNeeded = (needsAction && actionButton) || (isCustomOfferPending && isCustomer);

  const transitionClasses = classNames({
    [css.transitionActionNeeded]: isActionNeeded,
    [css.transitionCompact]: !hasExpandedContent && !isActionNeeded,
    [css.transition]: !isActionNeeded && hasExpandedContent,
  });

  // Check if this transition should show the invoice button
  const shouldShowInvoiceButton =
    !isCustomer &&
    (currentTransition === TRANSITION_RELEASE_FUNDS ||
      currentTransition === TRANSITION_JOB_DONE_RELEASE_FUNDS ||
      currentTransition === TRANSITION_JOB_ACCEPTED ||
      currentTransition === TRANSITION_SKIP_TO_REVIEWS ||
      currentTransition === TRANSITION_ACCEPT_JOB_INVOICE ||
      currentTransition === TRANSITION_JOB_DONE_ACCEPT_INVOICE) &&
    provider?.attributes?.profile?.publicData?.account_role !== 'freelancer';

  return (
    <div className={transitionClasses}>
      <div className={css.transitionTopRow}>
        <div className={css.bullet}>
          {(needsAction && actionButton) || (isCustomOfferPending && isCustomer) ? (
            <IoMdCheckmarkCircleOutline className={css.transitionBulletIconWarning} />
          ) : (
            <IoMdCheckmarkCircle className={css.transitionBulletIcon} />
          )}
        </div>
        <div className={css.transitionContentWrapper}>
          <div className={css.transitionContent}>
            <div>{transitionMessage}</div>
            <p className={css.transitionDate}>
              {formatDate(intl, todayString, transition.createdAt)}
            </p>
          </div>
          {shouldShowInvoiceButton && (
            <div className={css.showInvoiceButtonContainer}>
              <button className={css.showInvoiceButton} onClick={onOpenInvoiceModal}>
                <FormattedMessage id="TransactionSteps.invoiceLinkText" />
              </button>
            </div>
          )}
        </div>
      </div>
      {reviewComponent}
      {isCustomOfferTransition && customOfferData && isCustomOfferPending && isCustomer ? (
        <div className={css.actionNeededContent}>
          {/* <p className={css.actionNeededInstruction}>
            <FormattedMessage id="TransactionSteps.customerReviewOffer" />
          </p> */}
          <CustomOfferActivity
            customOfferData={customOfferData}
            isCustomer={isCustomer}
            transaction={currentTransaction}
            vatPreview={vatPreview}
            intl={intl}
            onOpenOfferModal={onOpenOfferModal}
            isPending={true}
          />
        </div>
      ) : (
        isCustomOfferTransition &&
        customOfferData && (
          <CustomOfferActivity
            customOfferData={customOfferData}
            isCustomer={isCustomer}
            transaction={currentTransaction}
            vatPreview={vatPreview}
            intl={intl}
            onOpenOfferModal={onOpenOfferModal}
            isPending={lastTransition === currentTransition}
          />
        )
      )}
      {needsAction && actionButton && (
        <div className={css.actionNeededContent}>
          {instructionText && <p className={css.actionNeededInstruction}>{instructionText}</p>}
          <div className={css.transitionActionButton}>{actionButton}</div>
        </div>
      )}
    </div>
  );
};

Transition.propTypes = {
  transition: propTypes.transition.isRequired,
  transaction: propTypes.transaction.isRequired,
  currentUser: propTypes.currentUser.isRequired,
  intl: intlShape.isRequired,
  onOpenReviewModal: func.isRequired,
  onOpenOfferModal: func,
  onOpenInvoiceModal: func,
  stepActions: object,
  actionInProgress: bool,
  ownReview: object,
  vatPreview: object,
};

const EmptyTransition = () => {
  return (
    <div className={css.transition}>
      <div className={css.bullet}>
        <p className={css.transitionContent}>•</p>
      </div>
      <div>
        <p className={css.transitionContent} />
        <p className={css.transitionDate} />
      </div>
    </div>
  );
};

// ReviewAction component for showing review prompt with action needed styling
const ReviewAction = ({ onOpenReviewModal, intl, transaction, currentUser }) => {
  const currentTransaction = ensureTransaction(transaction);
  const ownRole = getUserTxRole(currentUser.id, currentTransaction);
  const isCustomer = txRoleIsCustomer(ownRole);
  const lastTransition = currentTransaction.attributes.lastTransition;

  // Check if user has already left their review
  const userHasLeftReview = txIsInFirstReviewBy(transaction, isCustomer);

  // Don't show if user has already reviewed
  if (userHasLeftReview) {
    return null;
  }

  // Find when payment was released
  const paymentReleaseTransition = currentTransaction.attributes.transitions?.find(
    t =>
      t.transition === TRANSITION_RELEASE_FUNDS ||
      t.transition === TRANSITION_JOB_DONE_RELEASE_FUNDS
  );

  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });

  return (
    <div className={css.transitionActionNeeded}>
      <div className={css.transitionTopRow}>
        <div className={css.bullet}>
          <IoMdCheckmarkCircleOutline className={css.transitionBulletIconWarning} />
        </div>
        <div className={css.transitionContentWrapper}>
          <p className={css.transitionContent}>
            <FormattedMessage id="ActivityFeed.reviewActionTitle" />
          </p>
          {paymentReleaseTransition && (
            <p className={css.transitionDate}>
              {formatDate(intl, todayString, paymentReleaseTransition.createdAt)}
            </p>
          )}
        </div>
      </div>
      <div className={css.actionNeededContent}>
        <p className={css.actionNeededInstruction}>
          <FormattedMessage
            id={
              isCustomer
                ? 'ActivityFeed.reviewActionDescriptionCustomer'
                : 'ActivityFeed.reviewActionDescriptionProvider'
            }
          />
        </p>
        <div className={css.transitionActionButton}>
          <SecondaryButton onClick={onOpenReviewModal}>
            <FormattedMessage id="TransactionSteps.writeReviewButton" />
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};

ReviewAction.propTypes = {
  onOpenReviewModal: func.isRequired,
  intl: intlShape.isRequired,
  transaction: propTypes.transaction.isRequired,
  currentUser: propTypes.currentUser.isRequired,
};

const isMessage = item => item && item.type === 'message';

// Compare function for sorting an array containing messages and transitions
const compareItems = (a, b) => {
  const itemDate = item => (isMessage(item) ? item.attributes.createdAt : item.createdAt);
  return itemDate(a) - itemDate(b);
};

const organizedItems = (messages, transitions, hideOldTransitions) => {
  const items = messages.concat(transitions).sort(compareItems);
  if (hideOldTransitions) {
    // Hide transitions that happened before the oldest message. Since
    // we have older items (messages) that we are not showing, seeing
    // old transitions would be confusing.
    return dropWhile(items, i => !isMessage(i));
  } else {
    return items;
  }
};

// One compact row at the top of the feed: the package the client contacted the
// freelancer about (transaction.attributes.protectedData.enquiryPackage).
export const EnquiryPackageRow = ({ transaction, currentUser, intl }) => {
  const currentTransaction = ensureTransaction(transaction);
  const { title, pricingModel, price } = currentTransaction.attributes.protectedData.enquiryPackage;
  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });

  const ownRole = getUserTxRole(currentUser.id, currentTransaction);
  const isProvider = txRoleIsProvider(ownRole);
  const name = (
    <UserDisplayName
      user={isProvider ? currentTransaction.customer : currentTransaction.provider}
      intl={intl}
    />
  );

  return (
    <li className={classNames(css.transitionItem, css.Item)}>
      <div className={css.enquiryPackageRow}>
        <div className={css.bullet}>
          <IoMdCheckmarkCircle className={css.transitionBulletIcon} />
        </div>
        <div className={css.enquiryPackageContent}>
          <span className={css.enquiryPackageText}>
            <FormattedMessage
              id={
                isProvider
                  ? 'ActivityFeed.enquiryPackageProvider'
                  : 'ActivityFeed.enquiryPackageCustomer'
              }
              values={{ name, title, b: chunks => <b>{chunks}</b> }}
            />
          </span>
          <PricingModelBadge pricingModel={pricingModel} />
          <PackagePrice price={price} pricingModel={pricingModel} />
        </div>
        <p className={css.transitionDate}>
          {formatDate(intl, todayString, currentTransaction.attributes.createdAt)}
        </p>
      </div>
    </li>
  );
};

export const ActivityFeedComponent = props => {
  const {
    rootClassName,
    className,
    messages,
    transaction,
    currentUser,
    hasOlderMessages,
    onOpenReviewModal,
    onOpenOfferModal,
    onOpenInvoiceModal,
    vatPreview,
    onShowOlderMessages,
    fetchMessagesInProgress,
    intl,
    stepActions,
    actionInProgress,
    onManageDisableScrolling,
    onDownloadAttachment,
    downloadAttachmentInProgressIds,
    onOpenAttachment,
    onFetchAttachmentPreview,
    attachmentPreviewUrls,
    ownReview,
  } = props;

  const messagesEndRef = useRef(null);

  // URL of an external link the user clicked; drives the "leaving Freedomly" modal.
  const [pendingUrl, setPendingUrl] = useState(null);

  // Freedomly links open directly; external links are intercepted with a warning modal.
  const handleLinkClick = (e, url) => {
    if (isFreedomlyUrl(url)) {
      return;
    }
    e.preventDefault();
    setPendingUrl(url);
  };

  useEffect(() => {
    messagesEndRef.current.scrollIntoView({ block: 'end' });
  }, [messages]);

  const classes = classNames(rootClassName || css.root, className);

  const currentTransaction = ensureTransaction(transaction);
  const allTransitions = currentTransaction.attributes.transitions
    ? currentTransaction.attributes.transitions
    : [];

  // Filter to show only the newest custom offer transition
  const filterNewestCustomOffer = transitions => {
    const customOfferTransitions = [TRANSITION_MAKE_CUSTOM_OFFER, TRANSITION_MAKE_CUSTOM_PRO_OFFER];

    const customOffers = transitions.filter(t => customOfferTransitions.includes(t.transition));

    const otherTransitions = transitions.filter(
      t => !customOfferTransitions.includes(t.transition)
    );

    // If there are custom offers, only include the newest one
    if (customOffers.length > 0) {
      const newestCustomOffer = customOffers.reduce((newest, current) => {
        return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
      });
      return [...otherTransitions, newestCustomOffer];
    }

    return transitions;
  };

  const transitions = filterNewestCustomOffer(allTransitions);
  const currentCustomer = ensureUser(currentTransaction.customer);
  const currentProvider = ensureUser(currentTransaction.provider);
  const currentListing = ensureListing(currentTransaction.listing);

  const transitionsAvailable = !!(
    currentUser &&
    currentUser.id &&
    currentCustomer.id &&
    currentProvider.id &&
    currentListing.id
  );

  // combine messages and transaction transitions
  let items = organizedItems(messages, transitions, hasOlderMessages || fetchMessagesInProgress);

  // Check if we should add a review action item
  const lastTransition = currentTransaction.attributes.lastTransition;
  const reviewStartTransitions = [
    TRANSITION_RELEASE_FUNDS,
    TRANSITION_JOB_DONE_RELEASE_FUNDS,
    TRANSITION_JOB_ACCEPTED,
    TRANSITION_SKIP_TO_REVIEWS,
    TRANSITION_ACCEPT_JOB_INVOICE,
    TRANSITION_JOB_DONE_ACCEPT_INVOICE,
  ];
  const firstReviewTransitions = [TRANSITION_REVIEW_1_BY_CUSTOMER, TRANSITION_REVIEW_1_BY_PROVIDER];
  const secondReviewTransitions = [
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
  ];

  const hasStartedReviews =
    reviewStartTransitions.includes(lastTransition) ||
    firstReviewTransitions.includes(lastTransition);
  const hasBothReviews = secondReviewTransitions.includes(lastTransition);

  // Add review action item if reviews have started but not completed
  if (hasStartedReviews && !hasBothReviews && transitionsAvailable) {
    const ownRole = getUserTxRole(currentUser.id, currentTransaction);
    const isCustomer = txRoleIsCustomer(ownRole);
    const userHasLeftReview = txIsInFirstReviewBy(currentTransaction, isCustomer);

    // Only add if user hasn't left their review
    if (!userHasLeftReview) {
      // Create a synthetic item for the review action
      const reviewActionItem = {
        type: 'review-action',
        createdAt: new Date(), // Use current time to put it at the end
      };
      items = [...items, reviewActionItem];
    }
  }

  const showEnquiryPackage =
    isValidEnquiryPackage(currentTransaction.attributes.protectedData?.enquiryPackage) &&
    !hasOlderMessages &&
    !fetchMessagesInProgress &&
    transitionsAvailable;

  const transitionComponent = transition => {
    if (transitionsAvailable) {
      return (
        <Transition
          transition={transition}
          transaction={transaction}
          currentUser={currentUser}
          intl={intl}
          onOpenReviewModal={onOpenReviewModal}
          onOpenOfferModal={onOpenOfferModal}
          onOpenInvoiceModal={onOpenInvoiceModal}
          stepActions={stepActions}
          actionInProgress={actionInProgress}
          ownReview={ownReview}
          vatPreview={vatPreview}
        />
      );
    } else {
      return <EmptyTransition />;
    }
  };

  const messageComponent = message => {
    const isOwnMessage =
      message.sender &&
      message.sender.id &&
      currentUser &&
      currentUser.id &&
      message.sender.id.uuid === currentUser.id.uuid;
    const messageProps = {
      message,
      intl,
      onLinkClick: handleLinkClick,
      onDownloadAttachment,
      downloadAttachmentInProgressIds,
      onOpenAttachment,
      onFetchAttachmentPreview,
      attachmentPreviewUrls,
    };
    if (isOwnMessage) {
      return <OwnMessage {...messageProps} />;
    }
    return <Message {...messageProps} />;
  };

  const messageListItemClasses = classNames(transactionCss.messageItem, css.Item);

  const messageListItem = message => {
    return (
      <li id={`msg-${message.id.uuid}`} key={message.id.uuid} className={messageListItemClasses}>
        {messageComponent(message)}
      </li>
    );
  };

  const transitionListItemClasses = classNames(css.transitionItem, css.Item);

  const transitionListItem = transition => {
    if (isRelevantPastTransition(transition.transition)) {
      return (
        <li key={transition.createdAt} className={transitionListItemClasses}>
          {transitionComponent(transition)}
        </li>
      );
    } else {
      return null;
    }
  };

  const reviewActionListItem = () => {
    return (
      <li key="review-action" className={transitionListItemClasses}>
        <ReviewAction
          onOpenReviewModal={onOpenReviewModal}
          intl={intl}
          transaction={transaction}
          currentUser={currentUser}
        />
      </li>
    );
  };

  return (
    <>
      <ul className={classes} ref={messagesEndRef}>
        {hasOlderMessages ? (
          <li className={transactionCss.showOlderWrapper} key="show-older-messages">
            <button
              className={transactionCss.showOlderButton}
              onClick={onShowOlderMessages}
              type="button"
            >
              <FormattedMessage id="ActivityFeed.showOlderMessages" />
            </button>
          </li>
        ) : null}
        {showEnquiryPackage ? (
          <EnquiryPackageRow
            key="enquiry-package"
            transaction={currentTransaction}
            currentUser={currentUser}
            intl={intl}
          />
        ) : null}
        {items.map(item => {
          if (item.type === 'review-action') {
            return reviewActionListItem();
          } else if (isMessage(item)) {
            return messageListItem(item);
          } else {
            return transitionListItem(item);
          }
        })}
      </ul>
      <LeavingSiteModal
        isOpen={!!pendingUrl}
        url={pendingUrl}
        onClose={() => setPendingUrl(null)}
        onConfirm={() => {
          if (pendingUrl) {
            window.open(pendingUrl, '_blank', 'noopener,noreferrer');
          }
          setPendingUrl(null);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </>
  );
};

ActivityFeedComponent.defaultProps = {
  rootClassName: null,
  className: null,
  onManageDisableScrolling: () => {},
  onDownloadAttachment: () => {},
  downloadAttachmentInProgressIds: [],
  onOpenAttachment: () => {},
  onFetchAttachmentPreview: () => {},
  attachmentPreviewUrls: {},
  ownReview: null,
  vatPreview: null,
};

ActivityFeedComponent.propTypes = {
  rootClassName: string,
  className: string,

  currentUser: propTypes.currentUser,
  transaction: propTypes.transaction,
  messages: arrayOf(propTypes.message),
  hasOlderMessages: bool.isRequired,
  onOpenReviewModal: func.isRequired,
  onOpenOfferModal: func,
  onOpenInvoiceModal: func,
  vatPreview: object,
  onShowOlderMessages: func.isRequired,
  fetchMessagesInProgress: bool.isRequired,
  stepActions: object,
  actionInProgress: bool,
  onManageDisableScrolling: func,
  onDownloadAttachment: func,
  downloadAttachmentInProgressIds: arrayOf(string),
  onOpenAttachment: func,
  onFetchAttachmentPreview: func,
  attachmentPreviewUrls: object,
  ownReview: object,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ActivityFeed = injectIntl(ActivityFeedComponent);

export default ActivityFeed;
